import type { RibbonFlyoutAnchorProps } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon';
import {
    RibbonControlGroupDefinitionMLR,
    RibbonControlGroupDefinitionSLR,
} from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/components';
import type { Layout } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/components/RibbonControlGroupMLR';
import type { RibbonControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/Controls/RibbonControlProps';
import { computedFn } from 'mobx-utils/lib/computedFn';
import { getIsViewModeSelected } from 'owa-command-ribbon-store';
import loc from 'owa-localize';
import type { ComposeViewState } from 'owa-mail-compose-store';
import type { MailRibbonGroup } from 'owa-mail-ribbon-store-shared-types';
import type { RibbonGroupId, RibbonControlId } from 'owa-ribbon-ids';
import { MailRibbonTabId } from 'owa-ribbon-ids';
import type { RibbonControlDefinitionProps } from 'owa-mail-ribbon-utils';
import { defaultGroupStringMap, shouldShowRibbonGroup } from 'owa-mail-ribbon-utils';
import { CommandingViewMode } from 'owa-outlook-service-option-store/lib/store/schema/options/CommandingOptions';
import {
    createRibbonGroupMLR,
    createRibbonGroupSLR,
    createRibbonGroupToolbar,
} from './createRibbonGroup';
import { getControlsToDisplay } from './getControlsToDisplay';

/**
 * Retrieves the SLR groups within a given tab, as well as the actions that fall under each group.
 * @param tabId the MailRibbonTabId for the tab that is calling this function
 * @param groups the given tab's configuration as is saved in that tab's store
 * @param getControlDefinition function that returns the control definition given a controlId
 * @param props properties for ribbonIdToControlMap
 * @returns an object that contains the sub-groups and corresponding actions of the given tab
 */
export const getTabGroups = computedFn(
    (
        tabId: MailRibbonTabId,
        groups: MailRibbonGroup[],
        getControlDefinition: (
            controlId: RibbonControlId,
            props: RibbonControlDefinitionProps
        ) => RibbonControlProps,
        props: RibbonControlDefinitionProps
    ): RibbonControlGroupDefinitionSLR[] => {
        let tabGroups: RibbonControlGroupDefinitionSLR[] = new Array();

        for (const group of groups) {
            if (!shouldShowRibbonGroup(tabId, group.groupId, props)) {
                continue;
            }

            let groupDefinition: RibbonControlGroupDefinitionSLR = getIsViewModeSelected(
                CommandingViewMode.CommandBar
            )
                ? createRibbonGroupToolbar(tabId, group.groupId, props.popoutComposeViewState)
                : createRibbonGroupSLR(tabId, group.groupId, props.popoutComposeViewState);

            groupDefinition.title = getGroupTitle(group);

            // Get button objects that fall under the current group (stored in group)
            groupDefinition.controls = getControlsToDisplay(
                tabId,
                group.controlIds,
                getControlDefinition,
                props
            );
            tabGroups.push(groupDefinition);
        }

        return tabGroups;
    }
);

/**
 * Retrieves the MLR groups within a given tab, as well as the actions that fall under each group.
 * @param tabId the MailRibbonTabId for the tab that is calling this function
 * @param groups the given tab's configuration as is saved in that tab's store
 * @param getControlDefinition function that returns the control definition given a controlId
 * @param getGroupLayouts function that returns the layout(s) given a controlId, or undefined, if none
 * @param getCommonGroupFlyout function that returns the "collapse" flyoutAnchor definition for a controlId, or undefined, if none
 * @param props properties for ribbonIdToControlMap
 * @returns an object that contains the sub-groups and corresponding actions of the given tab
 */
export const getMLRTabGroups = computedFn(
    (
        tabId: MailRibbonTabId,
        groups: MailRibbonGroup[],
        getControlDefinition: (
            controlId: RibbonControlId,
            props: RibbonControlDefinitionProps
        ) => RibbonControlProps,
        getGroupLayouts: (
            groupId: RibbonGroupId,
            popoutComposeViewState?: ComposeViewState
        ) => Layout[] | undefined,
        getCommonGroupFlyout: (
            groupId: RibbonGroupId,
            popoutComposeViewState?: ComposeViewState
        ) => RibbonFlyoutAnchorProps | undefined,
        props: RibbonControlDefinitionProps
    ): RibbonControlGroupDefinitionMLR[] => {
        let tabGroups: RibbonControlGroupDefinitionMLR[] = new Array();

        for (const group of groups) {
            if (!shouldShowRibbonGroup(tabId, group.groupId, props)) {
                continue;
            }

            const groupDefinition: RibbonControlGroupDefinitionMLR = createRibbonGroupMLR(
                tabId,
                group.groupId,
                props.popoutComposeViewState
            );

            groupDefinition.title = getGroupTitle(group);

            // Get button objects that fall under the current group (stored in group)
            groupDefinition.controls = getControlsToDisplay(
                tabId,
                group.controlIds,
                getControlDefinition,
                props
            );

            // Get layouts (if any)
            groupDefinition.layouts = getGroupLayouts(group.groupId, props.popoutComposeViewState);

            // Get collapse Flyout info (if any)
            groupDefinition.commonGroupFlyout = getCommonGroupFlyout(
                group.groupId,
                props.popoutComposeViewState
            );

            tabGroups.push(groupDefinition);
        }

        return tabGroups;
    }
);

function getGroupTitle(group: MailRibbonGroup): string {
    // If groupName isn't undefined, that means user has changed the name away from the default group name.
    if (group.groupName != undefined) {
        return group.groupName;
    }

    if (defaultGroupStringMap.has(group.groupId)) {
        return loc(defaultGroupStringMap.get(group.groupId)!);
    }

    throw new Error('Group ' + group.groupId + ' was not added to defaultGroupStringMap.ts');
}
